import type {
  CreateTicketReplyRequest,
  CreateTicketReplyResponse,
  CreateTicketRequest,
  CreateTicketResponse,
  ListTicketAttachmentsResponse,
  ListTicketRepliesResponse,
  ListTicketsResponse,
  TicketStatusFilter,
} from '@nadoutong/contracts';
import { platformApiRequest, platformApiRequestBlob } from './client.ts';

/**
 * Typed wrappers around platform-api `/v1/tickets/*` (tenant #116).
 *
 * Wire notes straight from the contract
 * (`packages/contracts/src/public-tickets.ts`):
 *   - The list is `createdAt` DESCENDING; `limit` is clamped 1..100
 *     server-side and the response carries `total` for paging.
 *   - `status=all` equals omitting the filter, so `all` is never sent —
 *     the shipped desktop client does the same.
 *   - Replies return in `createdAt` ASCENDING order; callers render
 *     them as received and never sort.
 *   - Visibility is role-dependent by design: a `member` sees only the
 *     tickets they created, owner/admin see the whole org. Different
 *     row counts per role are contract behavior, not a bug.
 *   - Attachment list (`GET …/attachments`) is metadata only; content
 *     (`GET …/attachments/:id`) is raw bytes with
 *     `Cache-Control: private, no-store`. Callers must not hang the
 *     content URL on an `<img src>` — fetch through this module and
 *     mint a blob object URL.
 */

const BASE_PATH = '/v1/tickets';

export interface ListTicketsParams {
  readonly status: TicketStatusFilter;
  readonly limit: number;
  readonly offset: number;
}

export async function listTickets(
  params: ListTicketsParams,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListTicketsResponse> {
  const query = new URLSearchParams();
  query.set('limit', String(params.limit));
  query.set('offset', String(params.offset));
  if (params.status !== 'all') {
    query.set('status', params.status);
  }
  return platformApiRequest<ListTicketsResponse>(
    `${BASE_PATH}?${query.toString()}`,
    { signal },
    fetchImpl,
  );
}

export async function createTicket(
  body: CreateTicketRequest,
  fetchImpl: typeof fetch,
): Promise<CreateTicketResponse> {
  return platformApiRequest<CreateTicketResponse>(BASE_PATH, { method: 'POST', body }, fetchImpl);
}

export async function listTicketReplies(
  ticketId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListTicketRepliesResponse> {
  return platformApiRequest<ListTicketRepliesResponse>(
    `${BASE_PATH}/${encodeURIComponent(ticketId)}/replies`,
    { signal },
    fetchImpl,
  );
}

export async function createTicketReply(
  ticketId: string,
  body: CreateTicketReplyRequest,
  fetchImpl: typeof fetch,
): Promise<CreateTicketReplyResponse> {
  return platformApiRequest<CreateTicketReplyResponse>(
    `${BASE_PATH}/${encodeURIComponent(ticketId)}/replies`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function listTicketAttachments(
  ticketId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListTicketAttachmentsResponse> {
  return platformApiRequest<ListTicketAttachmentsResponse>(
    `${BASE_PATH}/${encodeURIComponent(ticketId)}/attachments`,
    { signal },
    fetchImpl,
  );
}

export async function getTicketAttachmentContent(
  ticketId: string,
  attachmentId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<Blob> {
  return platformApiRequestBlob(
    `${BASE_PATH}/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentId)}`,
    { signal },
    fetchImpl,
  );
}
